import React, { Fragment, useState, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XIcon, TrashIcon, PrinterIcon, CheckCircleIcon } from '@heroicons/react/outline';
import { useCart } from '../hooks/useCart';
import { useTranslation } from '../hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import usePDF from '../hooks/usePDF';
import usePokemonPDF from '../hooks/usePokemonPDF';
import Card from './Card';
import PokemonCard from './PokemonCard';
import ActivityIndicator from './ActivityIndicator';
import Badge from './Badge';
import PDFSettings from './PDFSettings';

export default function Cart() {
  const { cartOpened, closeCart, cart, clearCart } = useCart();
  const mtgPDF = usePDF();
  const pokemonPDF = usePokemonPDF();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('mtg');

  // Memoize the clearCart function to prevent unnecessary re-renders
  const handleClearCart = useCallback(() => {
    clearCart();
  }, [clearCart]);

  // Filter cards by TCG type
  const mtgCards = cart.filter((card) => !card?.details?.tcg || card?.details?.tcg === 'mtg');
  const pokemonCards = cart.filter((card) => card?.details?.tcg === 'pokemon');

  const onPrint = useCallback(() => {
    if (activeTab === 'pokemon') {
      pokemonPDF.downloadPDF(cart);
    } else {
      mtgPDF.downloadPDF(cart);
    }
  }, [activeTab, pokemonPDF, mtgPDF, cart]);

  // Get the current PDF settings based on active tab
  const downloadStatus = React.useMemo(() => (activeTab === 'pokemon' ? pokemonPDF.downloadStatus : mtgPDF.downloadStatus), [activeTab, pokemonPDF.downloadStatus, mtgPDF.downloadStatus]);

  // Memoize PDF settings to prevent unnecessary re-renders
  const outerPadding = React.useMemo(() => (activeTab === 'pokemon' ? pokemonPDF.outerPadding : mtgPDF.outerPadding), [activeTab, pokemonPDF.outerPadding, mtgPDF.outerPadding]);
  const setOuterPadding = React.useCallback(
    (value) => {
      if (activeTab === 'pokemon') {
        pokemonPDF.setOuterPadding(value);
      } else {
        mtgPDF.setOuterPadding(value);
      }
    },
    [activeTab, pokemonPDF, mtgPDF]
  );

  const betweenPadding = React.useMemo(() => (activeTab === 'pokemon' ? pokemonPDF.betweenPadding : mtgPDF.betweenPadding), [activeTab, pokemonPDF.betweenPadding, mtgPDF.betweenPadding]);
  const setBetweenPadding = React.useCallback(
    (value) => {
      if (activeTab === 'pokemon') {
        pokemonPDF.setBetweenPadding(value);
      } else {
        mtgPDF.setBetweenPadding(value);
      }
    },
    [activeTab, pokemonPDF, mtgPDF]
  );

  const format = React.useMemo(() => (activeTab === 'pokemon' ? pokemonPDF.format : mtgPDF.format), [activeTab, pokemonPDF.format, mtgPDF.format]);
  const setFormat = React.useCallback(
    (value) => {
      if (activeTab === 'pokemon') {
        pokemonPDF.setFormat(value);
      } else {
        mtgPDF.setFormat(value);
      }
    },
    [activeTab, pokemonPDF, mtgPDF]
  );

  const scale = React.useMemo(() => (activeTab === 'pokemon' ? pokemonPDF.scale : mtgPDF.scale), [activeTab, pokemonPDF.scale, mtgPDF.scale]);
  const setScale = React.useCallback(
    (value) => {
      if (activeTab === 'pokemon') {
        // Ensure value has % at the end for Pokemon PDF
        pokemonPDF.setScale(value.includes('%') ? value : `${value}%`);
      } else {
        mtgPDF.setScale(value);
      }
    },
    [activeTab, pokemonPDF, mtgPDF]
  );

  const CartHeader = React.memo(() => {
    return (
      <div className='px-4 sm:px-6'>
        <div className='flex items-start justify-between'>
          <div className='flex items-center space-x-2'>
            <Dialog.Title className='text-lg font-medium text-gray-900 dark:text-gray-50'>{t('cart.title')}</Dialog.Title>
          </div>
          <div className='flex items-center ml-3 h-7'>
            <Button size='icon' variant='ghost' onClick={closeCart}>
              <span className='sr-only'>{t('cart.closePanel')}</span>
              <XIcon className='w-6 h-6' aria-hidden='true' />
            </Button>
          </div>
        </div>
      </div>
    );
  });

  CartHeader.displayName = 'CartHeader';

  const CartList = React.memo(() => {
    return (
      <Tabs defaultValue='mtg' value={activeTab} onValueChange={setActiveTab} className='w-full'>
        <TabsList className='grid w-full grid-cols-2 mb-4'>
          <TabsTrigger value='mtg' className='relative flex items-center gap-2'>
            {t('cart.mtgTabLabel')}
            {mtgCards.length > 0 && <Badge>{mtgCards.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value='pokemon' className='relative flex items-center gap-2'>
            {t('cart.pokemonTabLabel')}
            {pokemonCards.length > 0 && <Badge>{pokemonCards.length}</Badge>}
          </TabsTrigger>
        </TabsList>
        <TabsContent value='mtg' className='mt-0'>
          {mtgCards.length > 0 ? (
            <ul className='grid grid-cols-2 gap-3'>
              {mtgCards.map((card) => (
                <li key={card.details.id}>
                  <Card card={card.details} showActiveStyles={false} showBuyButton={false} />
                </li>
              ))}
            </ul>
          ) : (
            <div className='text-center py-8'>
              <p className='text-muted-foreground'>{t('cart.emptyMtg')}</p>
            </div>
          )}
        </TabsContent>
        <TabsContent value='pokemon' className='mt-0'>
          {pokemonCards.length > 0 ? (
            <ul className='grid grid-cols-2 gap-3'>
              {pokemonCards.map((card) => (
                <li key={card.details.id}>
                  <PokemonCard card={card.details} showActiveStyles={false} showBuyButton={false} />
                </li>
              ))}
            </ul>
          ) : (
            <div className='text-center py-8'>
              <p className='text-muted-foreground'>{t('cart.emptyPokemon')}</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    );
  });

  CartList.displayName = 'CartList';

  const CartEmpty = React.memo(() => {
    return (
      <div>
        <p className='text-gray-500'>{t('cart.empty')}</p>
      </div>
    );
  });

  CartEmpty.displayName = 'CartEmpty';

  return (
    <Transition.Root show={cartOpened} as={Fragment}>
      <Dialog as='div' className='fixed inset-0 z-[100] overflow-hidden' onClose={closeCart}>
        <div className='absolute inset-0 overflow-hidden'>
          <Dialog.Overlay className='absolute inset-0' />

          <div className='fixed inset-y-0 right-0 flex max-w-full'>
            <Transition.Child
              as={Fragment}
              enter='transform transition ease-in-out duration-500'
              enterFrom='translate-x-full'
              enterTo='translate-x-0'
              leave='transform transition ease-in-out duration-500'
              leaveFrom='translate-x-0'
              leaveTo='translate-x-full'
            >
              <div className='relative w-screen md:max-w-xl'>
                <div className='flex flex-col h-full pt-6 pb-48 overflow-y-scroll shadow-xl bg-gray-50 dark:bg-gray-800'>
                  <CartHeader />
                  <div className='relative flex-1 px-4 mt-6 sm:px-6 pb-24'>{cart.length > 0 ? <CartList /> : <CartEmpty />}</div>
                  {cart.length > 0 && (
                    <div className='fixed bottom-0 right-0 z-50 flex flex-col flex-shrink-0 w-full max-w-xl px-4 py-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg'>
                      <PDFSettings
                        activeTab={activeTab}
                        t={t}
                        outerPadding={outerPadding}
                        setOuterPadding={setOuterPadding}
                        betweenPadding={betweenPadding}
                        setBetweenPadding={setBetweenPadding}
                        format={format}
                        setFormat={setFormat}
                        scale={scale}
                        setScale={setScale}
                      />
                      <div className='flex items-center justify-between mt-4'>
                        <Button size='sm' variant='outline' onClick={handleClearCart} className='flex items-center gap-2'>
                          <TrashIcon className='w-4 h-4' aria-hidden='true' />
                          <span>{t('cart.clearCardList')}</span>
                        </Button>
                        <Button onClick={onPrint} variant='primary' size='lg' className='min-w-[140px]'>
                          {downloadStatus === 'pending' ? (
                            <ActivityIndicator size={18} />
                          ) : (
                            <div className='flex items-center gap-2'>
                              {downloadStatus === 'success' ? (
                                <>
                                  <CheckCircleIcon className='w-5 h-5' aria-hidden='true' />
                                  <span>{t('cart.success')}</span>
                                </>
                              ) : (
                                <>
                                  <PrinterIcon className='w-5 h-5' aria-hidden='true' />
                                  <span>{t('cart.print')}</span>
                                </>
                              )}
                            </div>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
